/**
 * Merko Integrations — public API contract.
 *
 * Transcribed from the backend handoff (contract checked against `runtime.py`,
 * `service.py`, `registry.py`, `adapters.py`, `actions.py` and the CDK route
 * declarations on 2026-10-08). The handoff describes the *implemented backend*,
 * not a live-certified provider deployment, so nothing here may be read as
 * provider readiness.
 *
 * Two rules the types themselves cannot enforce, and which callers must keep:
 *
 *   • Fields typed `T | null` are emitted by the current public DTO but may be
 *     null when not applicable.
 *   • An unknown enum string is an error/refresh state, never an assumed
 *     success. `isKnown*()` guards below exist for exactly that check.
 */

export type AppId =
    | "mailchimp" | "wordpress" | "zoho" | "zendesk" | "shopify"
    | "hubspot" | "salesforce" | "googlesheets" | "airtable" | "typeform"
    | "klaviyo" | "brevo" | "gorgias" | "kit" | "omnisend"
    | "intercom" | "mailerlite" | "activecampaign" | "pipedrive";

/** Every catalog app ID, for runtime validation of a response. */
export const APP_IDS: readonly AppId[] = [
    "mailchimp", "wordpress", "zoho", "zendesk", "shopify",
    "hubspot", "salesforce", "googlesheets", "airtable", "typeform",
    "klaviyo", "brevo", "gorgias", "kit", "omnisend",
    "intercom", "mailerlite", "activecampaign", "pipedrive",
] as const;

/** The only keys a profile `mapping` may carry. */
export type ContactField =
    | "first_name" | "last_name" | "email"
    | "phone_number" | "whatsapp_number" | "messenger_id";

export const CONTACT_FIELDS: readonly ContactField[] = [
    "first_name", "last_name", "email",
    "phone_number", "whatsapp_number", "messenger_id",
] as const;

export type ConnectionStatus = "disconnected" | "active" | "paused_auth" | "disconnecting";
export type ProfileStatus = "enabled" | "disabled" | "paused_auth" | "paused_quota" | "failed";
export type JobStatus =
    | "queued" | "running" | "completed"
    | "completed_with_errors" | "failed" | "cancelled"
    | "paused_auth" | "paused_quota";
export type ActionStatus =
    | "pending" | "queued" | "executing"
    | "completed" | "failed" | "expired" | "outcome_unknown";

export const CONNECTION_STATUSES: readonly ConnectionStatus[] =
    ["disconnected", "active", "paused_auth", "disconnecting"] as const;
export const JOB_STATUSES: readonly JobStatus[] = [
    "queued", "running", "completed", "completed_with_errors",
    "failed", "cancelled", "paused_auth", "paused_quota",
] as const;
export const ACTION_STATUSES: readonly ActionStatus[] = [
    "pending", "queued", "executing", "completed", "failed", "expired", "outcome_unknown",
] as const;

/** Job statuses after which no further polling is useful. */
export const TERMINAL_JOB_STATUSES: readonly JobStatus[] = [
    "completed", "completed_with_errors", "failed", "cancelled", "paused_auth", "paused_quota",
] as const;

export const isKnownAppId = (value: unknown): value is AppId =>
    typeof value === "string" && (APP_IDS as readonly string[]).includes(value);
export const isKnownConnectionStatus = (value: unknown): value is ConnectionStatus =>
    typeof value === "string" && (CONNECTION_STATUSES as readonly string[]).includes(value);
export const isKnownJobStatus = (value: unknown): value is JobStatus =>
    typeof value === "string" && (JOB_STATUSES as readonly string[]).includes(value);
export const isKnownActionStatus = (value: unknown): value is ActionStatus =>
    typeof value === "string" && (ACTION_STATUSES as readonly string[]).includes(value);
export const isTerminalJob = (status: JobStatus): boolean =>
    (TERMINAL_JOB_STATUSES as readonly string[]).includes(status);

/** Every error response has this shape; branch on `code`, display `message`. */
export interface ApiError {
    error: { code: string; message: string };
}

export type Capability = "contact_import" | "scheduled_sync" | "content_connection";

export interface CatalogApp {
    id: AppId;
    name: string;
    /** Display only — never a request input. */
    toolkit: string;
    category: string;
    /** Descriptive, not a selectable scheme. */
    authentication: string;
    /** Display only — never a request input. */
    logoUrl: string;
    /**
     * Administrator setup state, not a user connection failure. An app is
     * available only with `enabled`, `contract_verified`, a nonempty auth
     * config ID and a dated toolkit version — none of which the frontend can
     * change.
     */
    available: boolean;
    connectionStatus: ConnectionStatus;
    /** Native connections are a separate system; never overwrite or migrate. */
    nativeConnectionStatus: string | null;
    /** e.g. Zendesk **Sell** for the native connection, vs Support for this app. */
    nativeProduct: string | null;
    capabilities: Capability[];
    supportsNative: boolean;
}

export interface Catalog {
    apps: CatalogApp[];
    /** Native or active Composio connections; Zendesk Sell deliberately excluded. */
    activeCount: number;
    /** Configured apps not already active through Composio. */
    availableCount: number;
    /** Only the disabled-handler fallback emits this, as `false`. */
    enabled?: boolean;
}

export interface SourceOption {
    id: string;
    name: string;
}

/**
 * Discovery result. Truncated at 100 options, normally with
 * `may_have_more: true`, so the UI must always allow an explicit ID.
 *
 * Apps with no discovery tool return the static `{id: "all"}` option plus
 * `required_fields` — and `{id: "all"}` must never be submitted as a source.
 */
export type Sources =
    | { sources: SourceOption[]; contact_import: false; may_have_more?: boolean }
    | {
        sources: SourceOption[];
        contact_import: true;
        required_fields?: string[];
        may_have_more?: boolean;
    };

/** App-specific; restrict by the per-app schema in `sources.ts`. */
export type Source = Record<string, string | number>;

export type Permission =
    | { mode: "source" }
    | { mode: "mapped"; field: string }
    | { mode: "attested"; attestation: string };

export type Frequency = "off" | "hourly" | "daily";

/** Exactly what POST and PATCH accept. PATCH is a full replacement. */
export interface ProfileInput {
    app_id: AppId;
    /** An existing Merko customer-group ID owned by the user — not a Composio ID. */
    group_id: number;
    source: Source;
    mapping: Partial<Record<ContactField, string>>;
    permission: Permission;
    frequency: Frequency;
}

export interface Profile extends ProfileInput {
    id: string;
    status: ProfileStatus;
    /** Unix seconds; 0 when no run is scheduled. */
    next_run_at: number;
    active_job: string | null;
    error_code: string | null;
    /** Response-only, on an attested policy. Must be stripped before PATCH. */
    permission: Permission & { attested_at?: number };
}

export type ContactPreview = Partial<Record<ContactField, string | null>> & {
    /** `null` is unknown — never present it as opted in. */
    permission: boolean | null;
};

export type Preview =
    | { profile_id: string; contacts: ContactPreview[]; sample_only: true }
    | { profile_id: string; message: string; requires_permission: true };

export type JobCountKey =
    | "inserted" | "updated" | "suppressed"
    | "conflict" | "permission_missing" | "skipped";

export interface ImportJob {
    id: string;
    profile_id: string;
    app_id: AppId;
    status: JobStatus;
    /** Sparse: an absent key means zero. This run only. */
    counts: Partial<Record<JobCountKey, number>>;
    created_at: number;
    updated_at: number;
    error_code: string | null;
    /** Cannot be requested by the frontend. */
    sync_mode: "full" | "incremental";
}

export type ActionOperation = "create_draft" | "create_template" | "update_contact";

export interface ExternalAction {
    id: string;
    app_id: AppId;
    operation: ActionOperation;
    /** As previewed; can contain HTML/content. Render as **text**, never live HTML. */
    fields: Record<string, string>;
    preview_hash: string;
    status: ActionStatus;
    expires_at: number;
    warning: string;
    error_code: string | null;
    result_id: string | null;
}

// ─── connection attempts ───────────────────────────────────────────────────

export type AttemptStatus =
    | "creating" | "pending" | "verifying" | "verified" | "completed" | "failed";

export const ATTEMPT_STATUSES: readonly AttemptStatus[] =
    ["creating", "pending", "verifying", "verified", "completed", "failed"] as const;

export const isKnownAttemptStatus = (value: unknown): value is AttemptStatus =>
    typeof value === "string" && (ATTEMPT_STATUSES as readonly string[]).includes(value);

export interface ConnectAttempt {
    attempt_id: string;
    /** Opened through the user's gesture. Never logged. */
    redirect_url: string;
    expires_at: number;
}

/**
 * The stored attempt's state. **Not** proof of a live active connection — only
 * the completion response and a refreshed catalog are.
 */
export interface AttemptState {
    id: string;
    app_id: AppId;
    status: AttemptStatus;
    expires_at: number;
}

export interface CompleteResult {
    app_id: AppId;
    /** A 200 can still say `pending`; poll before claiming success. */
    status: "active" | "pending";
}

export interface DisconnectResult {
    app_id: AppId;
    status: "disconnected";
    contacts_retained: true;
}

export interface DeleteProfileResult {
    deleted: true;
    contacts_retained: true;
}

/** Attempts expire ten minutes after creation. */
export const ATTEMPT_TTL_MS = 10 * 60 * 1000;
/** A write preview expires ten minutes after creation. */
export const ACTION_TTL_MS = 10 * 60 * 1000;
/** Backend cap on profiles per account. */
export const MAX_PROFILES = 100;
/** Backend cap on any JSON request body. */
export const MAX_BODY_BYTES = 32_000;
/** Longest string identifier any source or mapping value may carry. */
export const MAX_IDENTIFIER_LENGTH = 190;
